// Considered: folding this into PreferencesOrganism's accordion — rejected because it hosts a
// third-party widget with its own lifecycle plus two Center-owned sections; mixing it into
// Preferences would couple a Descope widget mount to the preferences save cycle. See spec
// Reuse Audit (specs/descope-auth-surface.md).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { UserProfile, useDescope } from '@descope/react-sdk';
import { useApiClients } from '../../../context/ApiClientsContext';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ConfirmDialogAtom, { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import { FormSectionAtom } from '../../atoms/FormSection/FormSectionAtom';
import { FormRowAtom } from '../../atoms/FormRow/FormRowAtom';
import { useTranslation } from 'localization';
import './SecurityOrganism.scss';

// CSS injected directly into the Descope widget's Shadow DOM via shadowRoot.appendChild().
// External SCSS cannot cross the Shadow DOM boundary. CSS custom properties DO inherit into
// Shadow DOM so design-system tokens resolve correctly, including dark-mode values.
// Note: Descope's injectStyle() has a bug with Constructable Stylesheets (replaceSync returns
// undefined → adoptedStyleSheets never updated). We bypass it and use a <style> element.
const WIDGET_SHADOW_CSS = `
  /* Hide the widget's own "Authentication Methods" heading — h-level tags and common div/span
     heading patterns all covered. FormSectionAtom's eyebrow already labels this section. */
  h1, h2, h3, h4, h5, h6 { display: none !important; }

  button:not([class*="logout"]):not([class*="danger"]):not([class*="delete"]) {
    font-size: var(--font-size-sm, 0.875rem) !important;
    font-weight: var(--font-weight-semibold, 600) !important;
    color: var(--primary-color, #377fc4) !important;
    background: transparent !important;
    border: none !important;
    cursor: pointer;
    padding: var(--spacing-xxs, 4px) var(--spacing-xs, 8px) !important;
    border-radius: var(--border-radius-sm, 4px) !important;
  }

  button:not([class*="logout"]):not([class*="danger"]):not([class*="delete"]):hover {
    background: color-mix(in srgb, var(--primary-color, #377fc4) 8%, transparent) !important;
  }

  button:not([class*="logout"]):not([class*="danger"]):not([class*="delete"]):focus-visible {
    outline: var(--focus-ring, 2px solid #377fc4);
    outline-offset: 2px;
  }

  li, [role="listitem"] {
    padding: var(--spacing-xs, 8px) 0 !important;
    margin: 0 !important;
  }

  hr {
    border: none !important;
    border-top: 1px solid var(--surface-border, rgba(0, 0, 0, 0.12)) !important;
    margin: var(--spacing-sm, 12px) 0 !important;
  }
`;

// The "Authentication Methods" heading might not be an h-level tag — the widget's HTML is
// CDN-served so we can't inspect it at build time. As a fallback, walk the open shadow DOM
// and hide the first leaf element whose full text is that label.
const AUTH_METHODS_HEADING_TEXT = 'Authentication Methods';

function hideAuthMethodsHeading(shadow: ShadowRoot): void {
  const walker = document.createTreeWalker(shadow, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.textContent?.trim() === AUTH_METHODS_HEADING_TEXT) {
      const leaf = node.parentElement;
      if (!leaf) break;
      // The text node's parentElement is the innermost element (often a span/p).
      // Walk UP until we find an ancestor that has siblings — that is the heading
      // BLOCK whose removal will also collapse the surrounding padding/margin.
      let target: HTMLElement = leaf;
      let cursor: HTMLElement | null = leaf.parentElement;
      while (cursor && cursor !== (shadow as unknown as HTMLElement)) {
        if (cursor.children.length > 1) break; // siblings exist — stop here
        target = cursor;
        cursor = cursor.parentElement;
      }
      target.style.display = 'none';
      break;
    }
    node = walker.nextNode();
  }
}

export interface McpConsent {
  consentId: string;
  clientName: string;
  scopes: string[];
  grantedAt: string;
}

export interface SecurityOrganismProps {
  /** Identity whose security surface is shown — always the caller's own (self-only routes). */
  identityId: string;
  className?: string;
}

/**
 * SecurityOrganism - Hosts the Descope-managed passkey/TOTP widget plus two
 * Center-owned sections: sign-out-everywhere and connected MCP clients
 * (Descope Inbound App consents). See specs/descope-auth-surface.md Phase 4.
 */
type DescopeTheme = 'light' | 'dark';

function readDescopeTheme(): DescopeTheme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

const SecurityOrganism: React.FC<SecurityOrganismProps> = ({ identityId, className = '' }) => {
  const { organizationClient } = useApiClients();
  const { t } = useTranslation();
  const { logout } = useDescope();
  const toastRef = useRef<Toast>(null);
  const widgetRef = useRef<HTMLElement>(null);
  const widgetReadyRef = useRef(false);

  // Mirrors the theme written to document.documentElement.dataset.theme so the
  // Descope widget receives the correct 'light'/'dark' prop at all times.
  const [descopeTheme, setDescopeTheme] = useState<DescopeTheme>(readDescopeTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setDescopeTheme(readDescopeTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [consents, setConsents] = useState<McpConsent[]>([]);
  const [isLoadingConsents, setIsLoadingConsents] = useState(true);
  const [consentsError, setConsentsError] = useState(false);
  const [revokingConsentId, setRevokingConsentId] = useState<string | null>(null);
  const [isSigningOutEverywhere, setIsSigningOutEverywhere] = useState(false);

  const loadConsents = useCallback(async () => {
    if (!identityId || !organizationClient) {
      setIsLoadingConsents(false);
      return;
    }

    setIsLoadingConsents(true);
    setConsentsError(false);
    try {
      const response: any = await organizationClient.get('/auth/mcp-consents');
      const list: McpConsent[] = Array.isArray(response) ? response : response?.data ?? [];
      setConsents(list);
    } catch (error) {
      console.error('[Security] Failed to load MCP consents:', error);
      setConsentsError(true);
    } finally {
      setIsLoadingConsents(false);
    }
  }, [identityId, organizationClient]);

  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  const injectWidgetStyles = useCallback(() => {
    const shadow = widgetRef.current?.shadowRoot;
    if (!shadow) return;
    // Remove any previously injected instance so re-runs on theme change don't stack.
    shadow.querySelector('#center-widget-style')?.remove();
    const style = document.createElement('style');
    style.id = 'center-widget-style';
    style.textContent = WIDGET_SHADOW_CSS;
    shadow.appendChild(style);
    // CSS h-selectors cover standard heading elements. Walk the shadow DOM text nodes as
    // a fallback in case the widget renders the heading as a styled div/span/p.
    hideAuthMethodsHeading(shadow);
  }, []);

  const handleWidgetReady = useCallback(() => {
    widgetReadyRef.current = true;
    injectWidgetStyles();
  }, [injectWidgetStyles]);

  // Re-inject when theme flips: the widget may reset its Shadow DOM stylesheet on theme change.
  useEffect(() => {
    if (widgetReadyRef.current) {
      injectWidgetStyles();
    }
  }, [descopeTheme, injectWidgetStyles]);

  const handleWidgetLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handleSignOutEverywhere = () => {
    confirmDialogAtom({
      header: t('security.sections.signOutEverywhere.confirmHeader'),
      message: t('security.sections.signOutEverywhere.confirmMessage'),
      variant: 'danger',
      accept: async () => {
        setIsSigningOutEverywhere(true);
        try {
          await organizationClient.post('/auth/sessions/logout-all', {});
          await logout();
        } catch (error) {
          console.error('[Security] Sign out everywhere failed:', error);
          toastRef.current?.show({
            severity: 'error',
            summary: t('security.error'),
            detail: t('security.sections.signOutEverywhere.error'),
            life: 3000,
          });
        } finally {
          setIsSigningOutEverywhere(false);
        }
      },
    });
  };

  const handleRevokeConsent = (consent: McpConsent) => {
    confirmDialogAtom({
      header: t('security.sections.mcpConsents.revokeConfirmHeader'),
      message: t('security.sections.mcpConsents.revokeConfirmMessage', { clientName: consent.clientName }),
      variant: 'danger',
      accept: async () => {
        setRevokingConsentId(consent.consentId);
        try {
          await organizationClient.delete(`/auth/mcp-consents/${consent.consentId}`);
          setConsents((prev) => prev.filter((c) => c.consentId !== consent.consentId));
          toastRef.current?.show({
            severity: 'success',
            summary: t('security.success'),
            detail: t('security.sections.mcpConsents.revokeSuccess'),
            life: 3000,
          });
        } catch (error) {
          console.error('[Security] Revoking the MCP consent failed:', error);
          toastRef.current?.show({
            severity: 'error',
            summary: t('security.error'),
            detail: t('security.sections.mcpConsents.revokeError'),
            life: 3000,
          });
        } finally {
          setRevokingConsentId(null);
        }
      },
    });
  };

  return (
    <div className={`security-organism ${className}`}>
      <Toast ref={toastRef} />
      <ConfirmDialogAtom />

      <div className="security-header">
        <h2>{t('security.header.title')}</h2>
        <p>{t('security.header.description')}</p>
      </div>

      <FormSectionAtom variant="card" label={t('security.sections.passkeys.title')} className="security-section-card">
        <p className="security-section-description">{t('security.sections.passkeys.description')}</p>
        <div className="security-widget-mount" data-testid="security-user-profile-widget">
          <UserProfile
            ref={widgetRef}
            widgetId="user-profile-widget"
            theme={descopeTheme}
            onReady={handleWidgetReady}
            onLogout={handleWidgetLogout}
          />
        </div>
      </FormSectionAtom>

      <FormSectionAtom
        variant="card"
        label={t('security.sections.signOutEverywhere.title')}
        className="security-section-card"
      >
        <FormRowAtom justify="between" wrap>
          <p className="security-section-description">{t('security.sections.signOutEverywhere.description')}</p>
          <ButtonAtom
            label={t('security.sections.signOutEverywhere.action')}
            icon="pi pi-sign-out"
            variant="danger"
            loading={isSigningOutEverywhere}
            onClick={handleSignOutEverywhere}
          />
        </FormRowAtom>
      </FormSectionAtom>

      <FormSectionAtom
        variant="card"
        label={t('security.sections.mcpConsents.title')}
        className="security-section-card"
      >
        <p className="security-section-description">{t('security.sections.mcpConsents.description')}</p>

        {isLoadingConsents && (
          <div className="security-mcp-consents-loading">
            <SkeletonAtom height="3rem" count={2} />
          </div>
        )}

        {!isLoadingConsents && consentsError && (
          <EmptyStateAtom
            icon="pi-exclamation-triangle"
            title={t('security.sections.mcpConsents.listError')}
            data-testid="security-mcp-consents-error"
          >
            <ButtonAtom label={t('common.retry')} variant="secondary" size="small" onClick={loadConsents} />
          </EmptyStateAtom>
        )}

        {!isLoadingConsents && !consentsError && consents.length === 0 && (
          <EmptyStateAtom
            icon="pi-shield"
            title={t('security.sections.mcpConsents.empty.title')}
            description={t('security.sections.mcpConsents.empty.description')}
            data-testid="security-mcp-consents-empty"
          />
        )}

        {!isLoadingConsents && !consentsError && consents.length > 0 && (
          <ul className="security-mcp-consents-list">
            {consents.map((consent) => (
              <li key={consent.consentId} className="security-mcp-consent-row">
                <FormRowAtom justify="between" wrap>
                  <div className="security-mcp-consent-info">
                    <strong>{consent.clientName}</strong>
                    <div className="security-mcp-consent-scopes">
                      {consent.scopes.map((scope) => (
                        <ChipAtom key={scope} label={scope} />
                      ))}
                    </div>
                  </div>
                  <ButtonAtom
                    label={t('security.sections.mcpConsents.revoke')}
                    variant="text"
                    size="small"
                    icon="pi pi-trash"
                    loading={revokingConsentId === consent.consentId}
                    onClick={() => handleRevokeConsent(consent)}
                    data-testid="security-revoke-consent-button"
                  />
                </FormRowAtom>
              </li>
            ))}
          </ul>
        )}
      </FormSectionAtom>
    </div>
  );
};

export default SecurityOrganism;
